import * as Sentry from '@sentry/react';
import { Typography } from 'src/components/new/Typography';
import { InvoiceResponse } from 'src/pages/dashboard/util';

interface InvoiceProps {
  invoiceData: InvoiceResponse;
  trackingNumber?: string;
  hideHeader?: boolean;
  noBorder?: boolean;
}

const mapInvoiceKeyToName: { [K in keyof InvoiceResponse]: string } = {
  salesTax: 'Sales Tax',
  subtotal: 'Subtotal',
  postageAndDelivery: 'Postage & Delivery',
  printCredits: 'Print Credits',
  convenienceFee: 'Convenience Fee',
  documentProcessing: 'Document Processing',
  cmEcfFees: 'CM/ECF Fees',
  total: 'Total',
};

const subtotalItemsKeys: (keyof InvoiceResponse)[] = ['printCredits', 'salesTax', 'convenienceFee'];
const mainItemsKeys: (keyof InvoiceResponse)[] = ['documentProcessing', 'postageAndDelivery', 'cmEcfFees'];

const Invoice: React.FC<InvoiceProps> = ({ invoiceData, trackingNumber, hideHeader = false, noBorder = false }) => {
  const missingFields = [...mainItemsKeys, ...subtotalItemsKeys, 'subtotal', 'total'].filter(
    (key) => invoiceData[key as keyof InvoiceResponse] == null,
  );
  if (missingFields.length) {
    Sentry.captureMessage(
      `Invoice missing fields${trackingNumber ? ` for job ${trackingNumber}` : ''}: ${missingFields.join(', ')}`,
      { level: 'warning', extra: { trackingNumber, invoiceData } },
    );
  }

  const mainItems = mainItemsKeys
    .map((key) => [key, (invoiceData[key] ?? 0).toFixed(2)])
    .filter((item) => item[1] !== '0.00');
  const subtotalItems = subtotalItemsKeys
    .map((key) => [key, (invoiceData[key] ?? 0).toFixed(2)])
    .filter((item) => item[1] !== '0.00');

  return (
    <>
      {!hideHeader && (
        <Typography
          sx={{
            marginTop: '0.9375rem',
            marginBottom: '0.9375rem',
            fontWeight: '500',
          }}
        >
          Invoice
        </Typography>
      )}
      <div
        style={{
          padding: noBorder ? 0 : '0.9375rem',
          border: noBorder ? 'none' : '0.0625rem solid #cac4d0',
          borderRadius: '0rem',
        }}
      >
        {mainItems.map((item, idx) => {
          return (
            <div key={`main_items_${idx}`} style={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography>{mapInvoiceKeyToName[item[0] as keyof typeof mapInvoiceKeyToName]}</Typography>
              <Typography>${item[1]}</Typography>
            </div>
          );
        })}
        <hr />
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            marginTop: '0.3125rem',
            marginBottom: '0.1875rem',
          }}
        >
          <Typography fontWeight={600}>Subtotal</Typography>
          <Typography fontWeight={600}>${(invoiceData.subtotal ?? 0).toFixed(2)}</Typography>
        </div>
        {subtotalItems.map((item, idx) => {
          return (
            <div key={`subtotal_items_${idx}`} style={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography>{mapInvoiceKeyToName[item[0] as keyof typeof mapInvoiceKeyToName]}</Typography>
              <Typography>
                {item[0] === 'printCredits' && '- '}${item[1]}
              </Typography>
            </div>
          );
        })}
        <hr />
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
          }}
        >
          <Typography fontSize={20} fontWeight={500}>
            Total
          </Typography>
          <Typography fontSize={20} fontWeight={500}>
            ${(invoiceData.total ?? 0).toFixed(2)}
          </Typography>
        </div>
      </div>
    </>
  );
};

export default Invoice;
